import React, { useContext, useState } from "react";
import { Button, FlexContainer, FormField, LottieLoadingIndicator, StandardModal, StandardModalActionGroup, Toast } from "src/components";
import { StyledCountryPhoneInput, TextLabel } from "../style";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { UserContext } from "src/scenes/App/UserContext";
import { EmailCampaignsContext } from "../../EmailCampaignsContext";
import { saveAudience } from "../../api/audienceApi";

const AddSingleContactFormId = "email-campaigns-add-single-contact-form";

function AddSingleContactForm(props) {
    const { countryCode } = useContext(UserContext);
    const { audienceName, cancelCreateNewAudience, initialFetchSettings } = useContext(EmailCampaignsContext);
    const {
        toggleAddSingleContact,
        contactFormData,
        setContactFormData,
        isEditing = false,
        onSaveSingleContact = () => { },
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handlePhoneNumber = (number) => {
        if (number !== "" && !number.startsWith("+")) {
            number = "+" + number;
        }

        const updatedFormData = { ...contactFormData, mobileNumber: number };
        setContactFormData(updatedFormData);
    };

    // Manual validation
    const validate = () => {
        const newErrors = {};
        if (!contactFormData.firstName.trim()) newErrors.firstName = "First name is required";
        if (!contactFormData.lastName.trim()) newErrors.lastName = "Last name is required";
        if (!contactFormData.email.trim()) {
            newErrors.email = "Email is required";
        } else {
            // Simple email regex pattern
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(contactFormData.email)) {
                newErrors.email = "Please enter a valid email address";
            }
        }
        return newErrors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Update main form data
        setContactFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors({ ...errors, [name]: "" });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        if (isEditing) return onSaveSingleContact();
        setIsLoading(true);
        try {
            const payload = {
                name: audienceName,
                contacts: [contactFormData],
            };
            const response = await saveAudience(payload);
            if (response) Toast.success("Audience has been successfully added.");
            setIsLoading(false);
            toggleAddSingleContact();
            cancelCreateNewAudience();
            setContactFormData({
                firstName: "",
                lastName: "",
                email: "",
                mobileNumber: "",
                reference: "",
            });
            initialFetchSettings();
        } catch (error) {
            setIsLoading(false);
            console.log(error);
            Toast.error(error.getApiErrorDetails().message);
        }
    };
    return (
        <StandardModal
            open={true}
            onClose={toggleAddSingleContact}
            widthMode="large"
            title="Add New Contact"
            description="Enter the contact details below to add this individual to your selected audience list. All fields except reference are required unless otherwise noted."
            footer={(
                <StandardModalActionGroup $pushRight>
                    <Button
                        type="button"
                        width="140px"
                        height="39px"
                        color="#000000"
                        border="1px solid #000"
                        onClick={() => toggleAddSingleContact()}
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Cancel
                    </Button>
                    {isLoading ? (
                        <LoadingIndicatorButton>
                            <LottieLoadingIndicator
                                height="2rem"
                                width="2rem"
                                margin="5px"
                                containerHeight="2rem"
                            />
                        </LoadingIndicatorButton>
                    ) : (
                        <Button
                            type="submit"
                            form={AddSingleContactFormId}
                            width="auto"
                            height="39px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="1px solid #171A22"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            {isEditing ? "Save Contact" : "Save Audience & Contact"}
                        </Button>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <form id={AddSingleContactFormId} onSubmit={handleSubmit}>
                <FlexContainer>
                    <FlexContainer
                        direction="row"
                        gap="20px"
                        justifyContent="space-between"
                    >
                        <FlexContainer width="100%">
                            <TextLabel fontSize="16px" fontWeight="bold">
                            First Name
                            </TextLabel>
                            <FormField
                                borderRadius="10px"
                                size="14px"
                                height="39px"
                                border={errors.firstName ? "1px solid red" : "1px solid #D8DEEC"}
                                backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                boxShadow="0px 3px 6px #00000029"
                                opacity="1"
                                align="left"
                                type="text"
                                placeholder={"First Name"}
                                placeholderColor="#808080"
                                placeholderSize="14px"
                                onChange={handleInputChange}
                                showErrors={true}
                                maxLength="100"
                                value={contactFormData.firstName}
                                name="firstName"
                                width="100%"
                            />
                            <TextLabel color="red" style={{ marginTop: -10 }}>
                                {errors.firstName}
                            </TextLabel>
                        </FlexContainer>
                        <FlexContainer width="100%">
                            <TextLabel fontSize="16px" fontWeight="bold">
                            Last Name
                            </TextLabel>
                            <FormField
                                borderRadius="10px"
                                size="14px"
                                height="39px"
                                border={errors.lastName ? "1px solid red" : "1px solid #D8DEEC"}
                                backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                boxShadow="0px 3px 6px #00000029"
                                opacity="1"
                                align="left"
                                type="text"
                                placeholder={"Last Name"}
                                placeholderColor="#808080"
                                placeholderSize="14px"
                                onChange={handleInputChange}
                                showErrors={true}
                                maxLength="100"
                                value={contactFormData.lastName}
                                name="lastName"
                                width="100%"
                            />
                            <TextLabel color="red" style={{ marginTop: -10 }}>
                                {errors.lastName}
                            </TextLabel>
                        </FlexContainer>
                        <FlexContainer width="100%">
                            <TextLabel fontSize="16px" fontWeight="bold">
                            Email
                            </TextLabel>
                            <FormField
                                borderRadius="10px"
                                size="14px"
                                height="39px"
                                border={errors.email ? "1px solid red" : "1px solid #D8DEEC"}
                                backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                boxShadow="0px 3px 6px #00000029"
                                opacity="1"
                                align="left"
                                type="text"
                                placeholder={"Email"}
                                placeholderColor="#808080"
                                placeholderSize="14px"
                                onChange={handleInputChange}
                                showErrors={true}
                                maxLength="100"
                                value={contactFormData.email}
                                name="email"
                                width="100%"
                            />
                            <TextLabel color="red" style={{ marginTop: -10 }}>
                                {errors.email}
                            </TextLabel>
                        </FlexContainer>
                    </FlexContainer>
                    <FlexContainer
                        direction="row"
                        gap="20px"
                    >
                        <FlexContainer width="auto">
                            <FlexContainer width="251px">
                                <div style={{ position: "relative" }}>
                                    <TextLabel fontSize="16px" fontWeight="bold" style={{ marginBottom: "15px" }}>
                                    Mobile (Optional)
                                    </TextLabel>
                                    <StyledCountryPhoneInput
                                        autoFormat={false}
                                        prefix={"+"}
                                        showErrors={true}
                                        errors={errors.mobile}
                                        country={countryCode.toLowerCase()}
                                        value={contactFormData.mobileNumber}
                                        onChange={number => handlePhoneNumber(number)}
                                        placeholder={"Mobile number"}
                                    />
                                    <TextLabel color="red" style={{ marginTop: -10 }}>
                                        {errors.mobileNumber}
                                    </TextLabel>
                                </div>
                            </FlexContainer>
                        </FlexContainer>
                        <FlexContainer width="100%">
                            <TextLabel fontSize="16px" fontWeight="bold">
                                {"Reference (Optional)"}
                            </TextLabel>
                            <FlexContainer width="251px" mWidth="100%" tWidth="100%">
                                <FormField
                                    borderRadius="10px"
                                    size="14px"
                                    height="39px"
                                    border={"1px solid #D8DEEC"}
                                    backgroundColor={"#FFFFFF 0% 0% no-repeat padding-box"}
                                    boxShadow="0px 3px 6px #00000029"
                                    opacity="1"
                                    align="left"
                                    type="text"
                                    placeholder={"Reference"}
                                    placeholderColor="#808080"
                                    onChange={handleInputChange}
                                    showErrors={true}
                                    maxLength="100"
                                    value={contactFormData?.reference}
                                    name="reference"
                                    width="100%"
                                />
                            </FlexContainer>
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
            </form>
        </StandardModal>
    );
}

export default AddSingleContactForm;
